import {createSlice} from "@reduxjs/toolkit";
import DateService from "../services/DateService";
import TransactionService from "../services/TransactionService";
import {updateWalletSuccess, mergeWalletsFromTransactions} from "./walletSlice";
import {loadCategoriesSuccess} from "./categorySlice";
import {loadCurrenciesSuccess} from "./currencySlice";
import {deleteBudgetSuccess, updateBudgetSuccess} from "./budgetSlice";
import {deleteTemplateSuccess} from "./templateSlice";


const initialState = {
    transactionsDays: [],
    latestDate: null,
    earliestDate: null
}

const isDraftWithoutId = (transaction) => transaction && transaction.id == null && transaction.state === "DRAFT";

const hasTemplateDraftKey = (transaction) =>
    transaction != null && transaction.templateId != null && transaction.templateDateKey != null;

const hasBudgetDraftKey = (transaction) =>
    transaction != null && transaction.budgetTemplateId != null && transaction.templateDateKey != null;

const isSameDraftLink = (existingDraft, savedTransaction) => {
    if (!isDraftWithoutId(existingDraft) || savedTransaction == null) {
        return false;
    }

    const templateMatch = hasTemplateDraftKey(existingDraft)
        && hasTemplateDraftKey(savedTransaction)
        && String(existingDraft.templateId) === String(savedTransaction.templateId)
        && String(existingDraft.templateDateKey) === String(savedTransaction.templateDateKey);
    if (templateMatch) {
        return true;
    }

    return hasBudgetDraftKey(existingDraft)
        && hasBudgetDraftKey(savedTransaction)
        && String(existingDraft.budgetTemplateId) === String(savedTransaction.budgetTemplateId)
        && String(existingDraft.templateDateKey) === String(savedTransaction.templateDateKey);
};

const removeTransactionByPredicate = (state, shouldRemove) => {
    const transactionsDays = state.transactionsDays
        .map((transactionsDay) => ({
            ...transactionsDay,
            transactions: transactionsDay.transactions.filter((transaction) => !shouldRemove(transaction))
        }))
        .filter((transactionsDay) => transactionsDay.transactions.length > 0);
    return {...state, transactionsDays};
};

const isSameLocalDraftTransaction = (existingDraft, targetDraft) => {
    if (!isDraftWithoutId(existingDraft) || !isDraftWithoutId(targetDraft)) {
        return false;
    }
    if (isSameDraftLink(existingDraft, targetDraft)) {
        return true;
    }
    return String(existingDraft.type ?? "") === String(targetDraft.type ?? "")
        && String(existingDraft.date ?? "") === String(targetDraft.date ?? "")
        && String(existingDraft.templateId ?? "") === String(targetDraft.templateId ?? "")
        && String(existingDraft.templateDateKey ?? "") === String(targetDraft.templateDateKey ?? "")
        && String(existingDraft.budgetTemplateId ?? "") === String(targetDraft.budgetTemplateId ?? "");
};

const transactionSlice = createSlice({
    name: 'transaction',
    initialState: initialState,
    reducers: {
        loadTransactionsSuccess(state, action) {
            const { transactionsDays: newDays, earliestDate: newEarliest, latestDate: newLatestDate } = action.payload;

            // Update earliest date (min) and latest date (max)
            const updatedEarliestDate = newEarliest != null ? (state.earliestDate != null && state.earliestDate < newEarliest ? state.earliestDate : newEarliest) : state.earliestDate;
            const updatedLatestDate = newLatestDate != null ? (state.latestDate != null && state.latestDate > newLatestDate ? state.latestDate : newLatestDate) : state.latestDate;

            // Create a map of existing days for quick lookup
            const existingDaysMap = TransactionService.transactionDaysToMap(state.transactionsDays);

            // Merge new days, updating existing ones
            newDays.forEach(newDay => {
                if (existingDaysMap.has(newDay.date)) {
                    // Update existing day by replacing transactions
                    const existingDay = existingDaysMap.get(newDay.date);
                    existingDaysMap.set(newDay.date, {
                        ...existingDay,
                        transactions: newDay.transactions // or merge: [...existingDay.transactions, ...newDay.transactions]
                    });
                } else {
                    // Add new day
                    existingDaysMap.set(newDay.date, { ...newDay });
                }
            });

            // Convert back to array and sort in DESC order
            const updatedTransactionsDays = TransactionService.mapToTransactionDays(existingDaysMap);

            // Return new state object
            return {
                ...state,
                transactionsDays: updatedTransactionsDays,
                earliestDate: updatedEarliestDate,
                latestDate: updatedLatestDate
            };
        },
        loadLateTransactionsSuccess(state, action) {
            return {...state,
                transactionDates: [...state.transactionDates, ...action.payload.transactionDates],
                latestDate: state.latestDate
                // previousPageKey: action.payload.previousPageKey
            }
        },
        updateTransactionSuccess(state, action) {
            const savedTransaction = action.payload;
            const day = DateService.dateTimeToDate(savedTransaction.date);

            let updatedDay = null;
            state.transactionsDays.forEach((transactionsDay) => {
                const existTransaction = transactionsDay.transactions.find(eachExistTransaction =>
                    eachExistTransaction.id === savedTransaction.id
                    || isSameDraftLink(eachExistTransaction, savedTransaction)
                );
                if(existTransaction != null) {
                    updatedDay = {
                        ...transactionsDay,
                        transactions: transactionsDay.transactions.filter(eachTransaction =>
                            eachTransaction.id !== savedTransaction.id && !isSameDraftLink(eachTransaction, savedTransaction)
                        )
                    };
                }
            });

            const daysMap = TransactionService.transactionDaysToMap([...state.transactionsDays]);
            if(updatedDay != null) {
                if(updatedDay.transactions.length === 0) {
                    daysMap.delete(updatedDay.date);
                } else {
                    daysMap.set(updatedDay.date, updatedDay);
                }
            }

            let transactionDay = daysMap.get(day);
            if(transactionDay) {
                daysMap.set(day, {
                    date: day,
                    transactions: TransactionService.add(transactionDay.transactions, savedTransaction)
                });
            } else {
                daysMap.set(day, {
                    date: day,
                    transactions: [savedTransaction]
                })
            }
            return {...state, transactionsDays: TransactionService.mapToTransactionDays(daysMap)}
        },
        deleteTransactionSuccess(state, action) {
            const id = action.payload;
            if (id == null) {
                return state;
            }
            return removeTransactionByPredicate(state, (transaction) => transaction.id == id);
        },
        deleteDraftTransactionSuccess(state, action) {
            const targetDraft = action.payload;
            return removeTransactionByPredicate(state, (transaction) => isSameLocalDraftTransaction(transaction, targetDraft));
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(deleteTemplateSuccess, (state, action) => {
                const templateId = action.payload;
                return removeTransactionByPredicate(state, (transaction) =>
                    isDraftWithoutId(transaction) && transaction.templateId != null && String(transaction.templateId) === String(templateId)
                );
            })
            .addCase(deleteBudgetSuccess, (state, action) => {
                const budgetId = action.payload;
                return removeTransactionByPredicate(state, (transaction) =>
                    isDraftWithoutId(transaction) && (
                        (transaction.budgetTemplateId != null && String(transaction.budgetTemplateId) === String(budgetId))
                        || (transaction.budget != null && String(transaction.budget) === String(budgetId))
                    )
                );
            });
    }
})

export const {
    loadTransactionsSuccess,
    updateTransactionSuccess,
    deleteTransactionSuccess,
    deleteDraftTransactionSuccess,
    loadPreviousTransactionsSuccess
} = transactionSlice.actions
export default transactionSlice.reducer


export const fetchEarlierTransactions = () => async (dispatch, getState) => {
    const earliestDate = getState().transactionSlice.earliestDate;
    return fetch('/api/transactions' + (earliestDate ? "?earliestDate=" + earliestDate : ""))
        .then((response) => {
            if (response.ok) {
                return response.json().then(json => {
                    dispatch(loadTransactionsSuccess(
                        {
                            transactionsDays: json.transactionsDays,
                            earliestDate: json.earliestDate,
                            latestDate: json.latestDate
                        }));
                    if (json.wallets && json.wallets.length > 0) {
                        dispatch(mergeWalletsFromTransactions(json.wallets));
                    }
                    if (json.categories && json.categories.length > 0) {
                        dispatch(loadCategoriesSuccess(json.categories));
                    }
                    if (json.currencies && json.currencies.length > 0) {
                        dispatch(loadCurrenciesSuccess(json.currencies));
                    }
                    if (json.budgets && json.budgets.length > 0) {
                        json.budgets.forEach(budget => {
                            dispatch(updateBudgetSuccess(budget));
                        });
                    }
                });
            }
        })
        .catch((error) => {
            console.log(error);
        });
}

export const fetchLaterTransactions = () => async (dispatch, getState) => {
    const latestDate = getState().transactionSlice.latestDate;
    return fetch('/api/transactions' + (latestDate ? "?latestDate=" + latestDate : ""))
        .then((response) => {
            if (response.ok) {
                return response.json().then(json => {
                    dispatch(loadTransactionsSuccess(
                        {
                            transactionsDays: json.transactionsDays,
                            earliestDate: json.earliestDate,
                            latestDate: json.latestDate
                        }));
                    if (json.wallets && json.wallets.length > 0) {
                        dispatch(mergeWalletsFromTransactions(json.wallets));
                    }
                    if (json.categories && json.categories.length > 0) {
                        dispatch(loadCategoriesSuccess(json.categories));
                    }
                    if (json.currencies && json.currencies.length > 0) {
                        dispatch(loadCurrenciesSuccess(json.currencies));
                    }
                    if (json.budgets && json.budgets.length > 0) {
                        json.budgets.forEach(budget => {
                            dispatch(updateBudgetSuccess(budget));
                        });
                    }
                });
            }
        })
        .catch((error) => {
            console.log(error);
        });
}

export const fetchFocusTransactions = (transactionId) => async (dispatch, getState) => {
    fetch('/api/transactions?focusTransaction=' + transactionId )
        .then((response) => {
            if (response.ok) {
                response.json().then(json => {
                    dispatch(loadTransactionsSuccess(
                        {
                            transactionsDays: json.transactionsDays,
                            earliestDate: json.earliestDate,
                            latestDate: json.latestDate
                        }));
                    if (json.wallets && json.wallets.length > 0) {
                        dispatch(mergeWalletsFromTransactions(json.wallets));
                    }
                    if (json.categories && json.categories.length > 0) {
                        dispatch(loadCategoriesSuccess(json.categories));
                    }
                    if (json.currencies && json.currencies.length > 0) {
                        dispatch(loadCurrenciesSuccess(json.currencies));
                    }
                    if (json.budgets && json.budgets.length > 0) {
                        json.budgets.forEach(budget => {
                            dispatch(updateBudgetSuccess(budget));
                        });
                    }
                });
            }
        })
        .catch((error) => {
            console.log(error);
        });
}

export const saveSetBalance = (setBalance, onSuccess) => async (dispatch, getState) => {
    fetch('/api/setBalances', {
        method: "PUT",
        body: JSON.stringify(setBalance),
        headers: {
            "Content-Type": "application/json",
        },
    })
        .then((response) => {
            if (response.ok) {
                response.json().then(json => {
                    const updatedWallets = json.wallets;
                    updatedWallets.forEach(wallet => {
                        dispatch(updateWalletSuccess(wallet));
                    })

                    const state = getState();
                    const transactionDay = DateService.dateTimeToDate(setBalance.date);

                    const isOutsideRange = state.transactionSlice.earliestDate && state.transactionSlice.latestDate &&
                        (transactionDay > state.transactionSlice.latestDate || transactionDay < state.transactionSlice.earliestDate);

                    if(isOutsideRange) {
                        dispatch(fetchFocusTransactions(json.transaction.id));
                    } else {
                        dispatch(updateTransactionSuccess(json.transaction));
                        if(onSuccess) {
                            onSuccess();
                        }
                    }
                });
            }
        })
        .catch((error) => {
            console.log(error);
        });
}

export const saveSetBudgetBalance = (setBalance, onSuccess) => async (dispatch, getState) => {
    fetch('/api/transactions/change-budget', {
        method: "PUT",
        body: JSON.stringify(setBalance),
        headers: {
            "Content-Type": "application/json",
        },
    })
        .then((response) => {
            if (response.ok) {
                response.json().then(json => {
                    const updatedBudgets = json.budgets;
                    updatedBudgets.forEach(budget => {
                        dispatch(updateBudgetSuccess(budget));
                    })

                    const state = getState();
                    const transactionDay = DateService.dateTimeToDate(setBalance.date);

                    const isOutsideRange = state.transactionSlice.earliestDate && state.transactionSlice.latestDate &&
                        (transactionDay > state.transactionSlice.latestDate || transactionDay < state.transactionSlice.earliestDate);

                    if(isOutsideRange) {
                        dispatch(fetchFocusTransactions(json.transaction.id));
                    } else {
                        dispatch(updateTransactionSuccess(json.transaction));
                        if(onSuccess) {
                            onSuccess();
                        }
                    }
                });
            }
        })
        .catch((error) => {
            console.log(error);
        });
}

const saveEndpointByType = (type) => {
    switch (type) {
        case 'INCOME': return '/api/transactions/income';
        case 'EXPENSE': return '/api/transactions/expense';
        case 'TRANSFER': return '/api/transactions/transfer';
        default: return '/api/transactions/expense';
    }
};

export const saveTransaction = (transaction, onSuccess, onWarning) => async (dispatch, getState) => {
    const url = saveEndpointByType(transaction.type);
    fetch(url, {
        method: "PUT",
        body: JSON.stringify(transaction),
        headers: {
            "Content-Type": "application/json",
        },
    })
        .then((response) => {
            if (response.ok) {
                response.json().then(json => {
                    const savedTransaction = json?.transaction ?? json;
                    const updatedWallets = json?.wallets ?? [];
                    updatedWallets.forEach(wallet => {
                        dispatch(updateWalletSuccess(wallet));
                    });
                    if (savedTransaction?.warning && onWarning) {
                        onWarning(savedTransaction.warning);
                    }
                    const state = getState();
                    const transactionDay = DateService.dateTimeToDate(savedTransaction.date);

                    const isOutsideRange = state.transactionSlice.earliestDate && state.transactionSlice.latestDate &&
                        (transactionDay > state.transactionSlice.latestDate || transactionDay < state.transactionSlice.earliestDate);

                    if(isOutsideRange) {
                        dispatch(fetchFocusTransactions(savedTransaction.id)).finally(() => {
                            if(onSuccess) {
                                onSuccess();
                            }
                        });
                    } else {
                        dispatch(updateTransactionSuccess(savedTransaction));
                        if(onSuccess) {
                            onSuccess();
                        }
                    }
                });
            }
        })
        .catch((error) => {
            console.log(error);
        });
}
//
export const deleteTransaction = (transaction, onSuccess) => async (dispatch, getState) => {
    fetch('/api/transactions?id=' + transaction.id, {
        method: "DELETE",
        // body: JSON.stringify(wallet),
    })
        .then((response) => {
            if (response.ok) {
                response.json().then(json => {
                    dispatch(deleteTransactionSuccess(transaction.id));
                    if(onSuccess) {
                        onSuccess();
                    }
                });
            }
        })
        .catch((error) => {
            console.log(error);
        });
}

export const deleteDraftTransaction = (transaction, onSuccess) => async (dispatch) => {
    dispatch(deleteDraftTransactionSuccess(transaction));
    if (onSuccess) {
        onSuccess();
    }
}
//
export const deleteSetBalance = (setBalance, onSuccess) => async (dispatch, getState) => {
    fetch('/api/setBalances?id=' + setBalance.id, {
        method: "DELETE",
    })
        .then((response) => {
            if (response.ok) {
                response.json().then(json => {
                    dispatch(deleteTransactionSuccess(setBalance.id));
                    if(onSuccess) {
                        onSuccess();
                    }
                });
            }
        })
        .catch((error) => {
            console.log(error);
        });
}
//
export const selectTransactionsDays = (state) =>
    state.transactionSlice.transactionsDays;

export const selectTransactionsEarliestDate = (state) =>
    state.transactionSlice.earliestDate;

export const selectTransactionsLatestDate = (state) =>
    state.transactionSlice.latestDate;
